// src/pages/hooks/usePremiumCheck.js
import { useState, useEffect } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firestore';

// ✅ Выносим функцию определения premium САМОСТОЯТЕЛЬНО (без зависимости от контекста)
const determinePremiumStatus = (firestoreData) => {
  if (!firestoreData) return false;
  
  // Проверяем subscription.active - это самый надежный способ!
  if (firestoreData.subscription?.active === true) {
    console.log('✅ Premium detected: subscription.active = true');
    return true;
  }
  
  // Проверяем статус подписки
  if (firestoreData.subscription?.status === 'active' || 
      firestoreData.subscription?.status === 'trialing' ||
      firestoreData.subscription?.status === 'complete') {
    console.log('✅ Premium detected: subscription.status =', firestoreData.subscription.status);
    return true;
  }
  
  // Проверяем все возможные поля для premium (обратная совместимость)
  const isPremium = 
    firestoreData.premium === true ||
    firestoreData.Premium === true ||
    firestoreData.isPremium === true ||
    firestoreData.is_premium === true;
  
  if (isPremium) {
    console.log('✅ Premium detected from legacy fields');
    return true;
  }
  
  // Проверяем текущий период подписки
  if (firestoreData.subscription?.currentPeriodEnd) {
    const endDate = new Date(firestoreData.subscription.currentPeriodEnd);
    const now = new Date();
    if (endDate > now) {
      console.log('✅ Premium detected: active subscription period');
      return true;
    }
  }
  
  return false;
};

// Получение имени коллекции в зависимости от типа пользователя
const getCollectionName = (userType) => {
  return userType === 'web' ? 'webUsers' : 'telegramUsers';
};

export const usePremiumCheck = (userId, userType = 'telegram') => {
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setIsPremium(false);
      setSubscriptionData(null);
      setLoading(false);
      return;
    }

    console.log(`🔔 Setting up real-time premium check for user: ${userId} (type: ${userType})`);
    setLoading(true);
    
    // Используем правильную коллекцию в зависимости от типа пользователя
    const collectionName = getCollectionName(userType);
    const userRef = doc(db, collectionName, userId);
    
    const unsubscribe = onSnapshot(userRef, 
      (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          
          // ✅ Используем локальную функцию
          const premiumStatus = determinePremiumStatus(userData);
          
          console.log('📊 Premium check result:', {
            userId,
            userType,
            premium: premiumStatus,
            subscription: userData.subscription,
            subscriptionActive: userData.subscription?.active,
            subscriptionStatus: userData.subscription?.status
          });
          
          setIsPremium(premiumStatus);
          setSubscriptionData(userData.subscription || null);
          setError(null);
        } else {
          console.log('📝 User document not found for premium check:', userId);
          setIsPremium(false);
          setSubscriptionData(null);
        }
        setLoading(false);
      }, 
      (error) => {
        console.error('❌ Error in premium check listener:', error);
        setIsPremium(false);
        setSubscriptionData(null);
        setError(error.message);
        setLoading(false);
      }
    );

    // Cleanup function
    return () => {
      console.log(`🔇 Unsubscribing from premium check for user: ${userId}`);
      unsubscribe();
    };
  }, [userId, userType]);

  // Функция для принудительной проверки статуса (для ручных вызовов)
  const refreshPremiumStatus = async () => {
    if (!userId) return false;
    
    setLoading(true);
    try {
      const collectionName = getCollectionName(userType);
      const userRef = doc(db, collectionName, userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const status = determinePremiumStatus(userData);
        setIsPremium(status);
        setSubscriptionData(userData.subscription || null);
        return status;
      }
    } catch (error) {
      console.error('Error refreshing premium status:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
    return false;
  };

  return { 
    isPremium, 
    loading,
    subscriptionData,
    error,
    refreshPremiumStatus,
    // Добавляем вспомогательные функции для удобства
    hasActiveSubscription: isPremium,
    isSubscriptionActive: isPremium,
    hasAccess: isPremium
  };
};